import React, { useState } from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';

import { PropertySelectField, PropertyTextInput } from '@paragon/client-tools';
import {
  Bearing,
  BearingCondition,
  bearingConditions,
  bearingTypes,
  isRelativeBearing,
} from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { hasEditBearingConditionsPermission } from '@paragon/login-tools';
import { getHangerListsRecord } from '@paragon/material-lists';
import { humanCase } from '@paragon/utilities';

import {
  addConnectorToBearing,
  changeBearingName,
  removeConnectorFromBearing,
  setBearingOnComponentDesign,
  updateConnectorOnBearing,
} from '../../../../store/actions/editBearingActions';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { getSingleSelectedItemEntryOrNull } from '../../../../store/selectors/getSelectedItemSelector';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { ConnectorsList } from '../elements/ConnectorsList';
import { BearingAbsoluteGeometrySubPanel } from './BearingAbsoluteGeometrySubPanel';
import BearingRelativeGeometrySubPanel from './BearingRelativeGeometrySubPanel';
import { PropertiesPanelMenuButton } from './PropertiesPanelMenuButton';
import { PropertiesSection } from './PropertiesSection';

type BearingPropertiesSection = 'geometry' | 'connectors';

function shouldAllowReleaseForUplift(bearingCondition: BearingCondition) {
  return bearingCondition === 'Automatic' || bearingCondition === 'HorizontalRoller';
}

export function BearingProperties() {
  const bearing = useAppSelector(getSingleSelectedItemEntryOrNull)!.item as Bearing;
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const isViewOnly = useAppSelector(propertiesShouldBeViewOnly);
  const canEditBearingConditions = useAppSelector(hasEditBearingConditionsPermission);
  const hangerLists = useAppSelector(getHangerListsRecord);
  const dispatch = useAppDispatch();

  const [activeSection, setActiveSection] = useState<BearingPropertiesSection | null>(null);

  const onEdit = (updatedProperties: Partial<Omit<Bearing, 'relativeGeometry'>>) => {
    dispatch(setBearingOnComponentDesign({ ...bearing, ...updatedProperties }, activeComponentDesign!.guid));
  };

  return (
    <PropertiesPanel
      title="Bearing Properties"
      titleButton={<PropertiesPanelMenuButton type="bearing" guid={bearing.guid} />}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', padding: '16px' }}>
        <PropertyTextInput
          sx={{ flex: 1 }}
          property="Name"
          disabled={isViewOnly}
          submit={(value) => dispatch(changeBearingName(bearing, activeComponentDesign!.guid, value))}
          value={bearing.name}
          required
        />
        <PropertySelectField
          sx={{ flex: 1, marginBottom: '16px' }}
          margin="dense"
          property="Bearing Type"
          value={bearing.bearingType}
          options={bearingTypes}
          getName={humanCase}
          disabled={isViewOnly}
          submit={(bearingType) => onEdit({ bearingType })}
        />
        {canEditBearingConditions ? (
          <>
            <PropertySelectField
              sx={{ flex: 1 }}
              margin="dense"
              property="Bearing Condition"
              value={bearing.bearingCondition}
              options={bearingConditions}
              getName={humanCase}
              disabled={isViewOnly}
              submit={(bearingCondition) =>
                onEdit({
                  bearingCondition,
                  releaseForUplift: shouldAllowReleaseForUplift(bearingCondition) ? bearing.releaseForUplift : false,
                })
              }
            />
            {shouldAllowReleaseForUplift(bearing.bearingCondition) && (
              <FormControlLabel
                control={
                  <Checkbox
                    color="default"
                    checked={bearing.releaseForUplift}
                    onChange={(_, checked) => onEdit({ releaseForUplift: checked })}
                  />
                }
                label="Release For Uplift"
                disabled={isViewOnly}
              />
            )}
          </>
        ) : null}
      </Box>
      <PropertiesSection
        title="Geometry"
        expanded={activeSection === 'geometry'}
        onExpand={() => setActiveSection('geometry')}
        onCollapse={() => setActiveSection(null)}
      >
        {isRelativeBearing(bearing) ? (
          <BearingRelativeGeometrySubPanel bearing={bearing} component={activeComponentDesign!.component} />
        ) : (
          <BearingAbsoluteGeometrySubPanel bearing={bearing} />
        )}
      </PropertiesSection>
      <PropertiesSection
        title="Connectors"
        expanded={activeSection === 'connectors'}
        onExpand={() => setActiveSection('connectors')}
        onCollapse={() => setActiveSection(null)}
      >
        <ConnectorsList
          connectors={bearing.connectors}
          hangerMaterialList={
            hangerLists ? hangerLists[activeComponentDesign!.materialSelection.hangerListGuid] : undefined
          }
          onAddConnector={() => {
            dispatch(addConnectorToBearing(bearing, activeComponentDesign!.guid));
          }}
          onUpdateConnector={(connector) => {
            dispatch(updateConnectorOnBearing(bearing, activeComponentDesign!.guid, connector));
          }}
          onRemoveConnector={(connectorGuid) => {
            dispatch(removeConnectorFromBearing(bearing, activeComponentDesign!.guid, connectorGuid));
          }}
          isViewOnly={isViewOnly}
        />
      </PropertiesSection>
    </PropertiesPanel>
  );
}
